import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, ExternalLink, Mic, Image as ImageIcon, RefreshCw, Users, Calendar } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const field = "h-10 w-full rounded-sm bg-white border border-sand-dark text-ink text-sm px-3 focus:outline-none focus:ring-2 focus:ring-terracotta";
const lbl = "font-mono text-[10px] uppercase tracking-widest text-ink-secondary mb-1 block";

const STATUS_LABEL = { idle: "No welcome yet", generating: "Recording…", ready: "Welcome ready", error: "Failed — retry" };

function CreateForm({ onCreated }) {
  const [f, setF] = useState({ name: "", trip_name: "", destination: "", market: "global", start_date: "", end_date: "", travellers: 2 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.name || !f.trip_name) { toast.error("Group name and trip name are required"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/trip-groups`, {
        method: "POST", headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...f, travellers: Number(f.travellers) || 1 }),
      });
      if (!res.ok) throw new Error();
      setF({ name: "", trip_name: "", destination: "", market: "global", start_date: "", end_date: "", travellers: 2 });
      toast.success("Trip group created");
      onCreated();
    } catch { toast.error("Couldn't create group"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-sand-dark bg-white p-5" data-testid="trip-create-form">
      <p className="font-display font-bold mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-terracotta" /> New trip group</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Group name *</label><input data-testid="trip-name-input" className={field} value={f.name} onChange={set("name")} placeholder="The Smith Family Everest Trek" /></div>
        <div><label className={lbl}>Trip / tour name *</label><input data-testid="trip-tour-input" className={field} value={f.trip_name} onChange={set("trip_name")} placeholder="Everest Base Camp" /></div>
        <div><label className={lbl}>Destination</label><input data-testid="trip-dest-input" className={field} value={f.destination} onChange={set("destination")} placeholder="Nepal" /></div>
        <div><label className={lbl}>Market</label>
          <select data-testid="trip-market-input" className={field} value={f.market} onChange={set("market")}>
            <option value="global">Global 🌏</option>
            <option value="au">Australia 🇦🇺</option>
          </select>
        </div>
        <div><label className={lbl}>Start date</label><input data-testid="trip-start-input" type="date" className={field} value={f.start_date} onChange={set("start_date")} /></div>
        <div><label className={lbl}>End date</label><input data-testid="trip-end-input" type="date" className={field} value={f.end_date} onChange={set("end_date")} /></div>
        <div><label className={lbl}>Travellers</label><input data-testid="trip-travellers-input" type="number" min="1" className={field} value={f.travellers} onChange={set("travellers")} /></div>
      </div>
      <button onClick={submit} disabled={busy} data-testid="trip-create-btn"
        className="mt-4 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-10 px-5 rounded-sm font-semibold text-sm transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create group
      </button>
    </div>
  );
}

function GroupCard({ g, onChange }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const link = `${window.location.origin}/trip/${g.token}`;

  const genWelcome = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/trip-groups/${g.id}/welcome`, { method: "POST", headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success("Raj's welcome is generating — refreshes when ready");
      onChange();
    } catch { toast.error("Couldn't start generation"); }
    finally { setBusy(false); }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/admin/trip-groups/${g.id}/hero`, { method: "POST", headers: authHeader(), body: fd });
      if (!res.ok) throw new Error();
      toast.success("Group photo updated");
      onChange();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${g.name}"? The client link will stop working.`)) return;
    try {
      const res = await fetch(`${API}/admin/trip-groups/${g.id}`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success("Group deleted"); onChange();
    } catch { toast.error("Delete failed"); }
  };

  const statusColor = { ready: "text-moss", generating: "text-terracotta", error: "text-destructive", idle: "text-ink-muted" }[g.welcome_status || "idle"];

  return (
    <div className="rounded-md border border-sand-dark bg-white p-4" data-testid={`trip-group-${g.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-bold text-ink truncate">{g.name}</p>
          <p className="text-sm text-ink-secondary">{g.trip_name}{g.destination ? ` · ${g.destination}` : ""}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1">{g.market === "au" ? "🇦🇺 Australia" : "🌏 Global"}</span>
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {g.travellers}</span>
            {g.start_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {g.start_date}</span>}
            <span className={`inline-flex items-center gap-1 font-medium ${statusColor}`}>
              {g.welcome_status === "generating" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {STATUS_LABEL[g.welcome_status || "idle"]}
            </span>
          </div>
        </div>
        <button onClick={del} data-testid={`trip-delete-${g.id}`} className="text-ink-muted hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex items-center gap-2 mt-3 bg-sand rounded-sm px-3 py-2">
        <code className="text-xs text-ink-secondary truncate flex-1" data-testid={`trip-link-${g.id}`}>{link}</code>
        <button onClick={async () => {
          try {
            if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(link); }
            else { throw new Error("unavailable"); }
            toast.success("Link copied");
          } catch {
            try {
              const ta = document.createElement("textarea");
              ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
              document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
              toast.success("Link copied");
            } catch { toast.error("Couldn't copy — " + link); }
          }
        }} data-testid={`trip-copy-${g.id}`} className="text-moss hover:text-moss-hover"><Copy className="w-4 h-4" /></button>
        <a href={link} target="_blank" rel="noreferrer" className="text-moss hover:text-moss-hover"><ExternalLink className="w-4 h-4" /></a>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={genWelcome} disabled={busy || g.welcome_status === "generating"} data-testid={`trip-welcome-${g.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-ink text-white text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
          {g.welcome_status === "ready" ? <RefreshCw className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
          {g.welcome_status === "ready" ? "Regenerate welcome" : "Generate Raj's welcome"}
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`trip-hero-${g.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-sand-dark text-ink text-xs font-semibold hover:border-moss transition-colors disabled:opacity-50">
          <ImageIcon className="w-3.5 h-3.5" /> {g.hero_image_url ? "Change photo" : "Group photo"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadHero} />
      </div>
    </div>
  );
}

export default function TripGroupsAdmin() {
  const [groups, setGroups] = useState(null);

  const load = () => fetch(`${API}/admin/trip-groups`, { headers: authHeader() })
    .then((r) => r.json()).then(setGroups).catch(() => setGroups([]));

  useEffect(() => { load(); }, []);

  // poll while any welcome is generating
  useEffect(() => {
    if (!groups?.some((g) => g.welcome_status === "generating")) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [groups]);

  return (
    <div data-testid="trip-groups-admin" className="space-y-5">
      <CreateForm onCreated={load} />
      {groups == null ? (
        <div className="text-ink-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading groups…</div>
      ) : groups.length === 0 ? (
        <p className="text-ink-secondary text-sm">No trip groups yet — create one above and share its link with the whole group.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {groups.map((g) => <GroupCard key={g.id} g={g} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
